import { useEffect, useMemo } from 'react';
import type { CatalogOption } from '../types/domain';

/**
 * Encadena el desplegable de área al de empresa en los formularios de datos propios.
 *
 * ── El defecto que corrige ─────────────────────────────────────────────────
 * Los formularios ofrecían `options.areas`, que es la lista para FILTRAR: una
 * entrada por nombre de área. Sirve para filtrar —«Operaciones» significa allí
 * «las Operaciones del grupo», y el backend expande el identificador a las cinco
 * empresas—, pero es lo contrario de lo que necesita un formulario: un caso
 * pertenece a UN área de UNA empresa, y el identificador que la lista deduplicada
 * ofrecía para «Operaciones» era el de una empresa arbitraria.
 *
 * Así se registró una fiscalización de Puerto Oxxean con el área «Operaciones» de
 * UCO. El backend hoy rechaza el par incoherente con `AREA_COMPANY_MISMATCH`, de
 * modo que el dato ya no se puede corromper; esto evita que el usuario llegue a
 * intentarlo, que es distinto de impedírselo después.
 *
 * ── Por qué también limpia el valor ────────────────────────────────────────
 * Cambiar de empresa deja el `areaId` anterior en el formulario, apuntando a un
 * área que ya no aparece en la lista. El `<select>` se ve vacío pero el valor
 * sigue ahí, y al guardar el backend lo rechaza por un motivo que la pantalla no
 * explica. Cuando el área seleccionada no pertenece a la empresa elegida, el
 * campo se vacía para que el usuario vuelva a elegir.
 *
 * @param areasByCompany Todas las áreas, cada una con su `companyId`.
 * @param companyId Empresa elegida en el formulario; vacía mientras no se elige.
 * @param areaId Área elegida, para detectar si quedó huérfana.
 * @param onAreaCleared Se invoca cuando el área deja de ser válida.
 */
export function useCompanyAreas(input: {
  areasByCompany: CatalogOption[] | undefined;
  companyId: string | undefined;
  areaId: string | undefined;
  onAreaCleared: () => void;
}): { areas: CatalogOption[]; disabled: boolean; hint: string | undefined } {
  const { areasByCompany, companyId, areaId, onAreaCleared } = input;

  const areas = useMemo(() => {
    if (companyId === undefined || companyId === '') return [];
    return (areasByCompany ?? []).filter((area) => area.companyId === companyId);
  }, [areasByCompany, companyId]);

  /**
   * Mientras el catálogo no ha llegado, NO se limpia nada.
   *
   * Al editar un registro existente el formulario nace con su `areaId` puesto. Si
   * se juzgara sin la lista cargada, toda área parecería huérfana —la lista
   * filtrada está vacía— y se borraría un valor correcto que el usuario no había
   * tocado. `undefined` significa «todavía no sé», que es distinto de «no hay».
   */
  const catalogLoaded = areasByCompany !== undefined;

  const orphaned =
    catalogLoaded &&
    areaId !== undefined &&
    areaId !== '' &&
    !areas.some((area) => area.id === areaId);

  useEffect(() => {
    if (orphaned) onAreaCleared();
    // `onAreaCleared` viene de `setValue`, estable entre renders en react-hook-form.
  }, [orphaned, onAreaCleared]);

  const disabled = companyId === undefined || companyId === '';

  return {
    areas,
    disabled,
    hint: disabled
      ? 'Elija primero la empresa: las áreas dependen de ella.'
      : !catalogLoaded
        ? 'Cargando las áreas de la empresa…'
        : areas.length === 0
          ? 'La empresa seleccionada no tiene áreas registradas.'
          : undefined,
  };
}
